import React, { useMemo } from "react";
import { Field, getFormValues } from "redux-form";
import { useSelector } from "react-redux";

import { ASSET_TYPES } from "utils/constants";
import { formatBytes } from "utils/common";
import { UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "social/constants/common";
import { getCardIndexFromName, isRequiredAssetField } from "social/utils";
import DynamicAssetSelect from "social/DynamicAssetSelect";
import DynamicInput from "social/DynamicInput";
import { SOCIAL_WIZARD } from "widgets/forms/list";

import { PINTEREST_IMAGE_CONSTRAINTS } from "social/validators/pinterest";
import { imposiumCompositionForCard } from "social/ImposiumCompSelector";
import { imposiumProject } from "social/ImposiumProjectSelector";

import "./CarouselCardForm.css";


const {
	MAX_LENGTH_TITLE,
	MAX_LENGTH_DESCRIPTION,
	MAX_LENGTH_LANDING_PAGE,
	MAX_IMAGE_BYTES,
	IMAGE_ACCEPT,
} = PINTEREST_IMAGE_CONSTRAINTS;

const PINTEREST_IMAGE_REQUIREMENTS = [
	{
		name: "File Size",
		description: `${formatBytes(MAX_IMAGE_BYTES)} Maximum`,
	},
	{
		name: "File Types",
		description: IMAGE_ACCEPT,
	},
	{
		name: "Dimensions",
		description: "1:1 or 2:3 aspect ratio",
	}
];

export const PINTEREST_CAROUSEL_FIELDS = {
	imposiumProject,
	imposiumComposition: imposiumCompositionForCard,
	image: {
		name: "image",
		label: "Image Asset",
		className: "full-width cypress-image-select",
		assetType: ASSET_TYPES.IMAGE,
		component: DynamicAssetSelect,
		defaultDynVarName: ({ name }) => `card${getCardIndexFromName(name)}_product`,
		requirements: PINTEREST_IMAGE_REQUIREMENTS,
		required: true,
		accept: IMAGE_ACCEPT,
	},
	title: {
		name: "title",
		label: "Title",
		className: "full-width cypress-headline",
		placeholder: "Enter a short headline",
		tip: {
			description: "Could be truncated after 40 characters",
			direction: "right",
		},
		component: DynamicInput,
		defaultDynVarName: ({ name }) => `card${getCardIndexFromName(name)}_title`,
		charMax: MAX_LENGTH_TITLE,
		// showCharCount: true,
	},
	description: {
		name: "description",
		label: "Description",
		className: "full-width cypress-description",
		placeholder: "Include additional details",
		component: DynamicInput,
		defaultDynVarName: ({ name }) => `card${getCardIndexFromName(name)}_description`,
		charMax: MAX_LENGTH_DESCRIPTION,
		// showCharCount: true,
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		className: "full-width cypress-landing-page",
		placeholder: "https://www.example.com",
		component: DynamicInput,
		defaultDynVarName: ({ name }) => `card${getCardIndexFromName(name)}_clickTag1`,
		charMax: MAX_LENGTH_LANDING_PAGE,
		// showCharCount: true,
		required: true,
	},
};


function fieldGenerator(props) {
	const {
		libraryId,
		cardPrefix,
		isViewOnly = false,
		assetType,
		navigationActions,
		showPristineErrors = false,
		doVersionsExist,
	} = props;

	return (name) => {
		const fieldProps = PINTEREST_CAROUSEL_FIELDS[name];

		let extraProps = {
			disabled: isViewOnly,
			showPristineErrors,
		};

		if(extraProps.disabled && fieldProps.placeholder){
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = "";
		}

		// If versions exist besides default, then dynamic toggle should be disabled
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
		}

		if(/image|video/gi.test(fieldProps.name)) {
			extraProps.libraryId = libraryId;
			extraProps.navigationActions = navigationActions;
			extraProps.creativeAssetType = assetType;
			extraProps.required = isRequiredAssetField(assetType);
		}

		if(cardPrefix === "cards[0]" && name === "imposiumComposition"){
			extraProps.showProjectName = false;
		}

		return (
			<Field
				key={fieldProps.label}
				{...fieldProps}
				{...extraProps}
				name = {`${cardPrefix}.${fieldProps.name}`}
			/>
		);
	};
}

export default function PinterestCarouselCardForm(props) {
	const {
		libraryId,
		navigationActions,
		adType,
		isViewOnly,
		showPristineErrors,
		doVersionsExist,
	} = props;

	const values = useSelector(state => getFormValues(SOCIAL_WIZARD)(state)?.[adType] || {});

	const assetType = values?.assetType;
	const currentCard = values?.currentCard || 0;
	const cardPrefix = `cards[${currentCard}]`;

	const generateFields = fieldGenerator({
		libraryId,
		cardPrefix,
		isViewOnly,
		assetType,
		showPristineErrors,
		navigationActions,
		doVersionsExist,
	});
	const fields = useMemo(() => Object.keys(PINTEREST_CAROUSEL_FIELDS).map(generateFields), [currentCard, assetType]);

	return (
		<div className="carousel-card-form">
			<div className="fields">
				{fields}
			</div>
		</div>
	);
}
